import { useState, useRef, useCallback, useEffect } from "react";
import type { Signature } from "@/types/quote";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PenLine, Type, X, RotateCcw } from "lucide-react";

interface SignatureModalProps {
	onSave: (signature: Signature) => void;
	onClose: () => void;
}

export function SignatureModal({ onSave, onClose }: SignatureModalProps) {
	const [mode, setMode] = useState<"draw" | "type">("draw");
	const [typedName, setTypedName] = useState("");
	const [signerName, setSignerName] = useState("");
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [isDrawing, setIsDrawing] = useState(false);
	const [hasDrawn, setHasDrawn] = useState(false);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		const rect = canvas.getBoundingClientRect();
		canvas.width = rect.width * 2;
		canvas.height = rect.height * 2;
		ctx.scale(2, 2);
		ctx.strokeStyle = "#e4e4e7";
		ctx.lineWidth = 2;
		ctx.lineCap = "round";
		ctx.lineJoin = "round";
	}, []);

	const getPos = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
		const canvas = canvasRef.current;
		if (!canvas) return { x: 0, y: 0 };
		const rect = canvas.getBoundingClientRect();
		return { x: e.clientX - rect.left, y: e.clientY - rect.top };
	}, []);

	const startDraw = useCallback(
		(e: React.MouseEvent<HTMLCanvasElement>) => {
			const canvas = canvasRef.current;
			const ctx = canvas?.getContext("2d");
			if (!ctx) return;
			setIsDrawing(true);
			setHasDrawn(true);
			const pos = getPos(e);
			ctx.beginPath();
			ctx.moveTo(pos.x, pos.y);
		},
		[getPos]
	);

	const draw = useCallback(
		(e: React.MouseEvent<HTMLCanvasElement>) => {
			if (!isDrawing) return;
			const canvas = canvasRef.current;
			const ctx = canvas?.getContext("2d");
			if (!ctx) return;
			const pos = getPos(e);
			ctx.lineTo(pos.x, pos.y);
			ctx.stroke();
		},
		[isDrawing, getPos]
	);

	const stopDraw = useCallback(() => {
		setIsDrawing(false);
	}, []);

	const clearCanvas = useCallback(() => {
		const canvas = canvasRef.current;
		const ctx = canvas?.getContext("2d");
		if (!ctx || !canvas) return;
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		setHasDrawn(false);
	}, []);

	const handleSave = () => {
		const today = new Date().toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric",
		});

		if (mode === "draw") {
			const canvas = canvasRef.current;
			if (!canvas || !hasDrawn) return;
			const data = canvas.toDataURL("image/png");
			onSave({
				type: "draw",
				data,
				name: signerName || "Signer",
				date: today,
			});
		} else {
			if (!typedName.trim()) return;
			onSave({
				type: "type",
				data: typedName.trim(),
				name: signerName || typedName.trim(),
				date: today,
			});
		}
	};

	const canSave = mode === "draw" ? hasDrawn && signerName.trim() : typedName.trim() && signerName.trim();

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
			<div className="bg-zinc-800 rounded-2xl shadow-2xl shadow-black/40 w-full max-w-lg mx-4 overflow-hidden border border-zinc-700/50">
				<div className="flex items-center justify-between px-6 py-4 border-b border-zinc-700/50">
					<h2 className="text-base font-medium text-zinc-100">Add Signature</h2>
					<button
						onClick={onClose}
						className="text-zinc-500 hover:text-zinc-300 transition-colors"
					>
						<X className="w-5 h-5" />
					</button>
				</div>

				<div className="p-6">
					<Tabs value={mode} onValueChange={(v) => setMode(v as "draw" | "type")}>
						<TabsList className="bg-zinc-700/50 mb-5">
							<TabsTrigger value="draw" className="gap-1.5">
								<PenLine className="w-3.5 h-3.5" />
								Draw
							</TabsTrigger>
							<TabsTrigger value="type" className="gap-1.5">
								<Type className="w-3.5 h-3.5" />
								Type
							</TabsTrigger>
						</TabsList>

						<TabsContent value="draw">
							<div className="relative border border-zinc-700 rounded-lg bg-zinc-900/50 mb-4">
								<canvas
									ref={canvasRef}
									className="w-full h-40 cursor-crosshair"
									onMouseDown={startDraw}
									onMouseMove={draw}
									onMouseUp={stopDraw}
									onMouseLeave={stopDraw}
								/>
								{!hasDrawn && (
									<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
										<p className="text-sm text-zinc-600">Draw your signature here</p>
									</div>
								)}
								{hasDrawn && (
									<button
										onClick={clearCanvas}
										className="absolute top-2 right-2 p-1.5 text-zinc-400 hover:text-zinc-200 bg-zinc-800 rounded-md border border-zinc-700 transition-colors"
									>
										<RotateCcw className="w-3.5 h-3.5" />
									</button>
								)}
							</div>
						</TabsContent>

						<TabsContent value="type">
							<div className="border border-zinc-700 rounded-lg bg-zinc-900/50 p-6 mb-4 min-h-[10rem] flex items-center justify-center">
								{typedName ? (
									<span
										className="text-4xl text-zinc-200"
										style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
									>
										{typedName}
									</span>
								) : (
									<span className="text-sm text-zinc-600">Your signature will appear here</span>
								)}
							</div>
							<Input
								placeholder="Type your full name"
								value={typedName}
								onChange={(e) => setTypedName(e.target.value)}
								className="border-zinc-700 bg-zinc-800 text-zinc-200 mb-4 placeholder:text-zinc-600"
							/>
						</TabsContent>
					</Tabs>

					<div className="space-y-3">
						<div>
							<label className="text-xs font-medium text-zinc-400 block mb-1.5">
								Signer's Name
							</label>
							<Input
								placeholder="Full legal name"
								value={signerName}
								onChange={(e) => setSignerName(e.target.value)}
								className="border-zinc-700 bg-zinc-800 text-zinc-200 placeholder:text-zinc-600"
							/>
						</div>
					</div>
				</div>

				<div className="flex justify-end gap-2 px-6 py-4 border-t border-zinc-700/50 bg-zinc-900/30">
					<Button variant="ghost" onClick={onClose} className="text-zinc-400 hover:text-zinc-200">
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						disabled={!canSave}
						className="bg-blue-600 hover:bg-blue-500 text-white"
					>
						Apply Signature
					</Button>
				</div>
			</div>
		</div>
	);
}
